import { DEFAULT_NEXT_SLIDE_THRESHOLD } from "Components/Shared/Inputs/CarouselPicker/constants";
import type { CarouselOption } from "Components/Shared/Inputs/CarouselPicker/types";
import { useRef } from "react";
import { CarouselContainer } from "./Components/CarouselContainer";
import { CarouselDots } from "./Components/CarouselDots";
import { CarouselInnerContainer } from "./Components/CarouselInnerContainer";
import { CarouselSlide } from "./Components/CarouselSlide";
import { CarouselTitle } from "./Components/CarouselTitle";
import { CarouselTrack } from "./Components/CarouselTrack";
import { useCarouselManager } from "./hooks/useCarouselManager";

type CarouselPickerProps<TValue = string> = {
  options: ReadonlyArray<CarouselOption<TValue>>;
  value: TValue;
  isInfinite?: boolean;
  nextSlideThreshold?: number;
  onSelect?: (option: CarouselOption<TValue>) => void;
  isDisabled?: boolean;
  hasEdgeBleed?: boolean;
};

export const CarouselPicker = <TValue extends string>({
  options,
  onSelect,
  value,
  isInfinite,
  isDisabled,
  hasEdgeBleed,
  nextSlideThreshold = DEFAULT_NEXT_SLIDE_THRESHOLD,
}: CarouselPickerProps<TValue>) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);

  const {
    currentIndex,
    dragDistance,
    hasDragged,
    trackEventHandlers,
    updateCurrentIndex,
  } = useCarouselManager({
    options,
    value,
    isInfinite,
    nextSlideThreshold,
    onSelect,
    carouselRef,
  });

  if (options.length === 0) {
    return null;
  }

  return (
    <CarouselContainer hasEdgeBleed={hasEdgeBleed}>
      <CarouselInnerContainer>
        <CarouselTrack
          ref={carouselRef}
          currentIndex={currentIndex}
          dragDistance={dragDistance}
          height={366}
          isDisabled={isDisabled}
          {...trackEventHandlers}
        >
          {options.map((option, index) => (
            <CarouselSlide
              key={option.value}
              option={option}
              isDisabled={isDisabled}
              index={index}
              currentIndex={currentIndex}
              hasDragged={hasDragged}
              onSelect={updateCurrentIndex}
            />
          ))}
        </CarouselTrack>
        <CarouselDots
          totalOptions={options.length}
          currentIndex={currentIndex}
        />
        <CarouselTitle>{options[currentIndex].title}</CarouselTitle>
      </CarouselInnerContainer>
    </CarouselContainer>
  );
};
